import React, { useState } from 'react';
import {
  MenuPopUp,
  MenuToggleButton,
} from 'src/components/regions-menu/toggle-menu';
import { Status } from 'src/graphql/types';
import { useMenuState } from 'src/hooks/use-menu-state';
import { MangoBrightBlue, White } from 'src/styles/colours';
import { DemiBold } from 'src/styles/fonts';
import styled from 'styled-components';
import { ApplyButton, ButtonContainer, CloseButton } from './menu-contents';

export const ALL_STATUSES: Array<NonNullable<Status>> = ['DELIVERED'];

const StyledPill = styled.span<{ active: boolean }>`
  display: inline-block;
  border-radius: 2rem;
  background-color: ${({ active }) => (active ? MangoBrightBlue : White)};
  padding: 0rem 1rem;
  line-height: 2.25rem;
  color: ${({ active }) => (active ? White : MangoBrightBlue)};
  border: 1px solid ${MangoBrightBlue};
  font-family: ${DemiBold};
  margin-left: 1rem;
  cursor: pointer;
`;

const StyledMenuPopUp = styled(MenuPopUp)`
  left: 1rem;
  right: auto;
  box-shadow: 0 2px 8px 0 rgba(66, 66, 74, 0.25);
  top: 50px;
  &:before {
    left: 25%;
    right: auto;
  }
`;

const StyledCheckbox = styled.input`
  margin-right: 1rem;
`;

const StyledLabel = styled.label`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  padding: 0.2rem 1rem;
  cursor: pointer;
`;

const Pill = ({ selectedVessel }: { selectedVessel: string[] | undefined }) => {
  let content = 'Vessel names';

  if (selectedVessel) {
    if (selectedVessel.length < 3 && selectedVessel.length > 0) {
      content = selectedVessel.map(vessel => vessel).join(', ');
    } else if (selectedVessel.length > 2) {
      content = `Vessel names: ${selectedVessel.length} selected`;
    }
    return (
      <StyledPill active={selectedVessel.length > 0}>{content}</StyledPill>
    );
  } else {
    return <StyledPill active={false}>{content}</StyledPill>;
  }
};

export const VesselFilter = ({
  vesseles,
  selectedVessel,
  applyStatusFilter,
}: {
  vesseles: any[];
  selectedVessel: string[];
  applyStatusFilter: (vesseles: string[]) => void;
}) => {
  const { displayed, setDisplayed, menuRef } = useMenuState();

  const [filters, setFilters] = useState(selectedVessel);

  const onToggle = (toggle: boolean, vessel: string) => {
    setFilters(
      toggle ? [...filters, vessel] : filters.filter(s => s !== vessel)
    );
  };

  const uniqueVessels = vesseles.filter((c, index) => {
    return vesseles.indexOf(c) === index;
  });

  const onToggleAll = (toggle: boolean) => {
    setFilters(toggle ? uniqueVessels : []);
  };

  const onCloseMenu = () => {
    setDisplayed(false);
    setFilters(uniqueVessels);
  };

  const onApply = () => {
    applyStatusFilter(filters);
    setDisplayed(false);
  };

  const vesselCount = vesseles.reduce(
    (acc, value) => ({
      ...acc,
      [value]: (acc[value] || 0) + 1,
    }),
    {}
  );

  return (
    <div ref={menuRef}>
      <MenuToggleButton displayed={displayed} setDisplayed={setDisplayed}>
        <Pill selectedVessel={selectedVessel} />
      </MenuToggleButton>
      <StyledMenuPopUp displayed={displayed} setDisplayed={setDisplayed}>
        <StyledLabel>
          <StyledCheckbox
            type="checkbox"
            data-type="filter-option"
            data-value="ALL"
            checked={filters.length === uniqueVessels.length}
            onChange={e => {
              return onToggleAll(e.target.checked);
            }}
          />
          (Select all)
        </StyledLabel>
        {Object.keys(vesselCount).map(names => (
          <div key={names}>
            <StyledLabel>
              <StyledCheckbox
                type="checkbox"
                data-type="filter-option"
                data-value={names}
                checked={filters.includes(names)}
                onChange={e => {
                  return onToggle(e.target.checked, names);
                }}
              />
              {names} ({vesselCount[names]})
            </StyledLabel>
          </div>
        ))}
        <ButtonContainer>
          <CloseButton onClick={onCloseMenu}>Close</CloseButton>
          <ApplyButton onClick={onApply} data-type="apply-button">
            Apply
          </ApplyButton>
        </ButtonContainer>
      </StyledMenuPopUp>
    </div>
  );
};
